import { tilePoster } from "../library/posterSize.js";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router";
import type { Channel, SeriesItem, VodItem } from "@hybrid-iptv/types";
import { cn, EmptyState, Section, Spinner } from "@hybrid-iptv/ui";
import { useChannels, useEpg, useProgramIndex, GUIDE_FIRST_HOURS } from "../epg/hooks.js";
import { useActiveProviderSourceId, activeProviderSourceId } from "../epg/api.js";
import { mirrorKeyFor } from "../library/mirrorResolver.js";
import { playVodItem } from "../library/vodHooks.js";
import { useCatalogQuery } from "../library/useCatalogQuery.js";
import { useSettled } from "../library/useSettled.js";
import type { CatalogQuery } from "../library/catalogQuery.js";
import { useFavoritesStore } from "../library/favoritesStore.js";
import { useHiddenChannelIds } from "../library/useFavorites.js";
import { usePlayerStore } from "../player/playerStore.js";
import { normalize, useUniversalSearch, type SearchHit, type SearchKind } from "../search/useUniversalSearch.js";
import { parseVoiceCommand, useVoiceSearch } from "../search/useVoiceSearch.js";
import { addRecent, suggestionsFor, useRecentSearches } from "../search/recentSearches.js";
import { useProfiles } from "../settings/profileStore.js";
import { localMarketFrom } from "../home/liveNowPicks.js";
import { YoutubeSearchSection } from "../search/YoutubeSearchSection.js";
import { useFavoriteRefs } from "../library/useFavorites.js";

type Filter = "all" | SearchKind;

const FILTERS: Array<{ key: Filter; label: string }> = [
  { key: "all", label: "All" },
  { key: "channel", label: "Channels" },
  { key: "program", label: "Guide" },
  { key: "movie", label: "Movies" },
  { key: "series", label: "Series" },
];

const NO_MOVIES: VodItem[] = [];
const NO_SERIES: SeriesItem[] = [];

/**
 * Universal search (Section 11). Everything runs against catalogs already on
 * the device, so results appear as fast as the user types and no keystroke
 * costs a provider connection.
 */
export function SearchPage() {
  // The topbar search lands on `/search?q=…`; honour that query (and keep the
  // input in sync when the URL changes, e.g. searching again from the topbar
  // while already on this page) instead of dropping it.
  const navigate = useNavigate();
  const location = useLocation();
  // Arriving from the top bar's box while typing: the viewer's keyboard is in THAT field, so the
  // page must not take the focus (and the keyboard) away from it.
  const arrivedTyping = (location.state as { live?: boolean } | null)?.live === true;
  const [searchParams] = useSearchParams();
  const [query, setQuery] = useState(() => searchParams.get("q") ?? "");
  const [filter, setFilter] = useState<Filter>("all");
  // The term whose YouTube results are asked for: set by Done, Enter, a recent search, a voice
  // search or the "Search all of YouTube" row, never by typing (search/YoutubeSearchSection.tsx).
  const [submitted, setSubmitted] = useState<string | null>(() => {
    const q = searchParams.get("q")?.trim() ?? "";
    const arrivedSubmitted = (location.state as { submitted?: boolean } | null)?.submitted === true;
    return q && (arrivedSubmitted || !arrivedTyping) ? q : null;
  });
  // The profile's own recent searches (search/recentSearches.ts): offered as you type, so a viewer
  // can pick the term instead of typing it all out.
  const profileId = useProfiles((state) => state.activeProfileId);
  const recent = useRecentSearches(profileId);
  const inputRef = useRef<HTMLInputElement>(null);

  // Voice search (Section 11 step 5): the transcript is parsed into a query +
  // optional filter, then fed through the same search as typing. Chromium-only
  // (Web Speech API) — other browsers simply don't render the mic button.
  const voice = useVoiceSearch((text) => {
    const { query: voiceQuery, filter: voiceFilter } = parseVoiceCommand(text);
    if (!voiceQuery) return;
    setQuery(voiceQuery);
    if (voiceFilter) setFilter(voiceFilter);
    commitRecent(voiceQuery);
    setSubmitted(voiceQuery);
  });

  const sourceId = useActiveProviderSourceId();
  const { data: channelData, isLoading: channelsLoading } = useChannels(sourceId);
  const channels = useMemo(() => channelData?.channels ?? [], [channelData]);
  const epg = useEpg(sourceId, channels, GUIDE_FIRST_HOURS);
  const programIndex = useProgramIndex(epg.data, channels);
  // Movies and series are asked for, not held (stage B): the device answers
  // the top 40 of each from native SQLite, scored exactly as below, and the
  // website runs the same query in memory. A short pause after typing keeps a
  // fast typist from queueing a query per keystroke on a slow TV.
  const needle = useSettled(normalize(query), 200);
  const searching = needle.length >= 2;
  const movieQuery = useMemo<CatalogQuery>(() => ({ kind: "vod", search: needle, limit: 40 }), [needle]);
  const seriesQuery = useMemo<CatalogQuery>(() => ({ kind: "series", search: needle, limit: 40 }), [needle]);
  const movies = useCatalogQuery<VodItem>(sourceId, movieQuery, searching);
  const series = useCatalogQuery<SeriesItem>(sourceId, seriesQuery, searching);
  // Still typing counts as loading, so "no results" never flashes mid-pause.
  const vodLoading = normalize(query) !== needle || (searching && (movies.isLoading || series.isLoading));

  const hydrated = useFavoritesStore((s) => s.hydrated);
  const hiddenChannelIds = useHiddenChannelIds();

  // The viewer's home market, read (as Home's Live Now does) from the stations they favourite and
  // watch: "On the guide" then shows their own local station for a show, not every city's.
  const favChannelRefs = useFavoriteRefs("channel");
  const recentChannels = usePlayerStore((s) => s.recentChannels);
  const market = useMemo(() => {
    const names = recentChannels.map((channel) => channel.name);
    if (favChannelRefs.length > 0) {
      const wanted = new Set(favChannelRefs);
      for (const channel of channels) if (wanted.has(channel.id)) names.push(channel.name);
    }
    return localMarketFrom(names);
  }, [recentChannels, favChannelRefs, channels]);

  const results = useUniversalSearch({
    query,
    market,
    channels,
    programIndex,
    movies: (searching && movies.data?.items) || NO_MOVIES,
    series: (searching && series.data?.items) || NO_SERIES,
    hiddenChannelIds: hydrated ? hiddenChannelIds : undefined,
  });

  // TV-first: land focus in the field so a remote can type immediately via the
  // on-screen keyboard, without hunting for the input first.
  useEffect(() => {
    if (!arrivedTyping) inputRef.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const incoming = searchParams.get("q") ?? "";
    setQuery(incoming);
    // The top bar's Done/Enter lands here with submitted set; live typing from it does not.
    if ((location.state as { submitted?: boolean } | null)?.submitted === true && incoming.trim()) setSubmitted(incoming.trim());
    // Topbar voice search hands off a content-type filter ("find action
    // movies" → filter=movie); honor it only when it is a real filter.
    const f = searchParams.get("filter");
    if (f === "all" || f === "channel" || f === "program" || f === "movie" || f === "series") {
      setFilter(f);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, location.key]);

  function commitRecent(value: string) {
    addRecent(profileId, value);
  }

  function playChannel(channel: Channel) {
    const store = usePlayerStore.getState();
    store.pushRecent({
      id: channel.id,
      name: channel.name,
      number: channel.number,
      streamUrl: channel.streamUrl,
      altStreamUrls: channel.altStreamUrls,
    });
    void store.playSource({
      url: channel.streamUrl,
      type: "live",
      title: channel.name,
      altUrls: channel.altStreamUrls,
      mirrorKey: mirrorKeyFor(activeProviderSourceId(), channel.id),
    });
    commitRecent(query);
  }

  function activate(hit: SearchHit) {
    if (hit.kind === "channel" && hit.channel) return playChannel(hit.channel);
    if (hit.kind === "program" && hit.channel) return playChannel(hit.channel);
    if (hit.kind === "movie" && hit.movie) {
      playVodItem(hit.movie.streamUrl, hit.movie.rawTitle, hit.movie.posterUrl ?? undefined, hit.movie.tmdbId);
      commitRecent(query);
      return;
    }
    if (hit.kind === "series" && hit.series) {
      // Series need season/episode selection, which lives on the Series page.
      commitRecent(query);
      navigate(`/series?q=${encodeURIComponent(hit.series.rawTitle)}`);
    }
  }

  // Everything saved while nothing is typed; the ones that match once something is.
  const suggested = suggestionsFor(recent, query);
  const loading = channelsLoading || vodLoading;
  const sections: Array<{ key: SearchKind; title: string; hits: SearchHit[] }> = [
    { key: "channel", title: "Channels", hits: results.channels },
    { key: "program", title: "On the guide", hits: results.programs },
    { key: "movie", title: "Movies", hits: results.movies },
    { key: "series", title: "Series", hits: results.series },
  ];
  const visible = sections.filter((s) => (filter === "all" || filter === s.key) && s.hits.length > 0);
  const showEmpty = query.trim().length >= 2 && results.total === 0 && !loading;

  return (
    <div className="page search-page">
      <form
        className="search-bar"
        role="search"
        onSubmit={(event) => {
          // Done on the on-screen keyboard and Enter both land here (components/TvKeyboard.tsx).
          event.preventDefault();
          const term = query.trim();
          if (term.length < 2) return;
          commitRecent(term);
          setSubmitted(term);
        }}
      >
        <input
          ref={inputRef}
          type="search"
          className="search-input"
          placeholder="Search channels, movies, series, guide…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label="Search"
        />
        {query && (
          <button type="button" className="search-clear" onClick={() => { setQuery(""); inputRef.current?.focus(); }} aria-label="Clear search">
            ✕
          </button>
        )}
        {voice.supported && (
          <button
            type="button"
            className={cn("search-mic", voice.listening && "search-mic--listening")}
            aria-pressed={voice.listening}
            aria-label={voice.listening ? "Stop voice search" : "Search by voice"}
            title={voice.listening ? "Stop listening" : "Search by voice"}
            onClick={() => (voice.listening ? voice.stop() : voice.start())}
          >
            <svg viewBox="0 0 24 24" aria-hidden>
              <path d="M12 15a3 3 0 0 0 3-3V6a3 3 0 1 0-6 0v6a3 3 0 0 0 3 3zm5-3a5 5 0 0 1-10 0H5a7 7 0 0 0 6 6.92V21h2v-2.08A7 7 0 0 0 19 12h-2z" />
            </svg>
          </button>
        )}
      </form>

      {voice.error && <p className="search-voice-status" role="status">{voice.error}</p>}

      <div className="search-filters" role="tablist" aria-label="Result type">
        {FILTERS.map((f) => {
          const count =
            f.key === "all"
              ? results.total
              : sections.find((s) => s.key === f.key)?.hits.length ?? 0;
          return (
            <button
              key={f.key}
              role="tab"
              aria-selected={filter === f.key}
              className={cn("search-filter", filter === f.key && "search-filter--active")}
              onClick={() => setFilter(f.key)}
            >
              {f.label}
              {query.trim().length >= 2 && <span className="search-filter-count">{count}</span>}
            </button>
          );
        })}
      </div>

      {suggested.length > 0 && (
        <Section title="Recent searches">
          <div className="search-recent">
            {suggested.map((term) => (
              <button
                key={term}
                type="button"
                className="search-recent-chip"
                onClick={() => {
                  setQuery(term);
                  commitRecent(term);
                  setSubmitted(term);
                }}
              >
                {term}
              </button>
            ))}
          </div>
        </Section>
      )}

      {query.trim().length < 2 && (
        <EmptyState
          icon="🔍"
          title="Search everything"
          body="Type at least two characters to search across your channels, the guide, movies, and series."
        />
      )}

      {loading && query.trim().length >= 2 && (
        <div className="search-loading">
          <Spinner /> <span>Loading your catalog…</span>
        </div>
      )}

      {showEmpty && (
        <EmptyState
          icon="🤷"
          title={`No results for "${query.trim()}"`}
          body="Try fewer words, or check a different result type above."
        />
      )}

      {visible.map((section) => (
        <Section key={section.key} title={`${section.title} (${section.hits.length})`}>
          <div className="search-results">
            {section.hits.map((hit) => (
              <SearchResultRow key={`${hit.kind}:${hit.id}`} hit={hit} onActivate={() => activate(hit)} />
            ))}
          </div>
        </Section>
      ))}

      {filter === "all" && query.trim().length >= 2 && (
        <YoutubeSearchSection
          query={query}
          submitted={submitted}
          onSubmit={(term) => {
            commitRecent(term);
            setSubmitted(term);
          }}
        />
      )}
    </div>
  );
}

function SearchResultRow({ hit, onActivate }: { hit: SearchHit; onActivate: () => void }) {
  const isFavorite = useFavoritesStore((s) =>
    hit.kind === "program" ? false : s.isFavorite(hit.kind as never, hit.id),
  );
  const toggleFavorite = useFavoritesStore((s) => s.toggleFavorite);
  const favoritable = hit.kind !== "program";

  return (
    <div className="search-result">
      <button className="search-result-main" onClick={onActivate} aria-label={`Open ${hit.title}`}>
        {hit.posterUrl ? (
          <img className="search-result-art" src={tilePoster(hit.posterUrl)} alt="" loading="lazy" decoding="async" />
        ) : (
          <span className="search-result-art search-result-art--placeholder" aria-hidden="true">
            {hit.kind === "channel" ? "📺" : hit.kind === "program" ? "🗓" : "🎬"}
          </span>
        )}
        <span className="search-result-text">
          <span className="search-result-title">{hit.title}</span>
          {hit.subtitle && <span className="search-result-subtitle">{hit.subtitle}</span>}
        </span>
      </button>
      {favoritable && (
        <button
          className={cn("search-result-fav", isFavorite && "search-result-fav--on")}
          aria-pressed={isFavorite}
          aria-label={isFavorite ? `Remove ${hit.title} from favorites` : `Add ${hit.title} to favorites`}
          onClick={() => toggleFavorite(hit.kind as never, hit.id)}
        >
          {isFavorite ? "★" : "☆"}
        </button>
      )}
    </div>
  );
}
